/**
 * Select Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { forwardRef, useId } from "react";
import { cn } from "../utils/cn";
import type { SelectProps } from "./Select.types";
import { selectVariants } from "./Select.variants";

// Re-exported so `import { selectVariants } from "@plerity/ui-core"` (and the
// component barrel `./Select`) keep working after the leaf-module extraction.
export { selectVariants };

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { className, variant, size, label, error, helperText, id, options, placeholder, children, required, ...props },
    ref
  ) => {
    const generatedId = useId();
    const selectId = id ?? generatedId;
    const effectiveVariant = error ? "error" : variant;

    return (
      <div className="space-y-1">
        {label && (
          <label htmlFor={selectId} className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            {label}
            {required && (
              <span className="ml-0.5 text-danger-strong" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            required={required}
            aria-required={required || undefined}
            className={cn(selectVariants({ variant: effectiveVariant, size }), className)}
            aria-invalid={!!error}
            aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options
              ? options.map((opt) => (
                  <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                    {opt.label}
                  </option>
                ))
              : children}
          </select>
          {/* Chevron icon */}
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>
        {error && (
          <p id={`${selectId}-error`} className="text-sm text-danger-strong" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${selectId}-helper`} className="text-sm text-gray-500 dark:text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = "Select";
